import { browserSupportsWebAuthnAutofill, startAuthentication, WebAuthnAbortService } from '@simplewebauthn/browser';
import { initDatabase } from './dbService';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

/**
 * Login - Backend Auto-Register wenn User nicht existiert
 */
export async function login(username, password) {
  try {
    if (!navigator.onLine) {
      return { success: false, error: 'Keine Internetverbindung. Login ist nur online möglich.' };
    }

    const response = await fetch(`${API_BASE_URL}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password })
    });

    const data = await parseResponseJson(response, 'Login fehlgeschlagen');
    await saveSession(data);
    return { 
      success: true, 
      user: data.user,
      profileComplete: data.user.profileComplete 
    };
  } catch (error) {
    console.error('Backend login failed:', error);
    return { success: false, error: error.message || 'Verbindung zum Server fehlgeschlagen' };
  }
}

async function postJson(path, body, fallback) {
  return parseResponseJson(await fetch(`${API_BASE_URL}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body)
  }), fallback);
}

const isAbort = (error) => ['NotAllowedError', 'AbortError'].includes(error?.name);

async function passkeyLogin(autofill) {
  if (!navigator.onLine) {
    return { success: false, error: 'Keine Internetverbindung. Login ist nur online möglich.' };
  }
  let options;
  try {
    ({ options } = await postJson('/auth/passkey/login/options', {}, 'Anmeldung mit Passkey nicht möglich'));
  } catch (error) {
    // Noch keine Nutzeraktion – Aufrufer kann das z. B. bei der Autovervollständigung still übergehen
    return { success: false, error: error.message || 'Verbindung zum Server fehlgeschlagen', beforePrompt: true };
  }
  try {
    let response;
    try {
      response = await startAuthentication({ optionsJSON: options, useBrowserAutofill: autofill });
    } catch (error) {
      if (isAbort(error)) return { success: false, cancelled: true };
      throw new Error('Der Passkey konnte auf diesem Gerät nicht verwendet werden.', { cause: error });
    }
    const data = await postJson('/auth/passkey/login/verify', { response }, 'Anmeldung mit Passkey fehlgeschlagen');
    await saveSession(data);
    return { success: true, user: data.user, profileComplete: data.user.profileComplete };
  } catch (error) {
    console.error('Passkey login failed:', error);
    return { success: false, error: error.message || 'Verbindung zum Server fehlgeschlagen' };
  }
}

/**
 * Anmelden mit Passkey: das Gerät fragt Fingerabdruck/Gesicht/PIN ab und liefert das Konto mit.
 * Ergebnis wie bei login(); { cancelled: true }, wenn der Nutzer den Dialog schließt.
 */
export const loginWithPasskey = () => passkeyLogin(false);

/** Passkeys in der Vorschlagsliste des E-Mail-Felds anbieten (Feld braucht autocomplete="username webauthn") */
export async function passkeyAutofillSupported() {
  try {
    return await browserSupportsWebAuthnAutofill();
  } catch {
    return false;
  }
}

/**
 * Wartet, bis der Nutzer im E-Mail-Feld einen Passkey auswählt. Endet mit { cancelled: true },
 * wenn stopPasskeyAutofill() aufgerufen oder eine andere Passkey-Abfrage gestartet wird.
 */
export const loginWithPasskeyAutofill = () => passkeyLogin(true);

export const stopPasskeyAutofill = () => WebAuthnAbortService.cancelCeremony();

/**
 * Session in IndexedDB speichern
 */
async function saveSession(data) {
  const db = await initDatabase();
  const loginTime = new Date();

  await db.put('auth', {
    key: 'session_token',
    value: data.token,
    expiresAt: data.expiresAt,
    updatedAt: loginTime.toISOString()
  });

  await db.put('auth', {
    key: 'user_info',
    value: data.user,
    updatedAt: loginTime.toISOString()
  });

  await db.put('auth', {
    key: 'login_timestamp',
    value: loginTime.toISOString(),
    updatedAt: loginTime.toISOString()
  });

  window.dispatchEvent(new Event('auth-changed'));
}

/**
 * Logout
 */
export async function logout() {
  const db = await initDatabase();
  // „Zuletzt verwendet“ gehört zum Nutzer, nicht zum Gerät (Schlüssel siehe recentService.js)
  try { localStorage.removeItem('btmx.recent'); } catch { /* egal */ }

  // Sitzung auf dem Server beenden (erscheint dann nicht mehr unter „Angemeldete Geräte“); offline egal
  const token = (await db.get('auth', 'session_token'))?.value;
  if (token && navigator.onLine) {
    fetch(`${API_BASE_URL}/auth/logout`, { method: 'POST', headers: { Authorization: `Bearer ${token}` }, keepalive: true }).catch(() => {});
  }
  
  await db.delete('auth', 'session_token');
  await db.delete('auth', 'user_info');
  await db.delete('auth', 'login_timestamp');

  window.dispatchEvent(new Event('auth-changed'));
}

/**
 * Prüft ob User eingeloggt ist UND Profil vollständig
 */
export async function isAuthenticated() {
  try {
    const db = await initDatabase();
    const token = await db.get('auth', 'session_token');

    if (!token || !token.value) {
      return false;
    }

    // Ablauf prüfen
    if (token.expiresAt) {
      const expiresAt = new Date(token.expiresAt);
      if (expiresAt < new Date()) {
        await logout();
        return false;
      }
    }

    // Profil-Vollständigkeit prüfen
    const userInfo = await db.get('auth', 'user_info');
    const user = userInfo?.value;
    
    if (!user) {
      return false;
    }

    // User muss Vorname UND Nachname haben
    if (!user.firstname || !user.lastname || 
        user.firstname.trim() === '' || user.lastname.trim() === '') {
      return false;
    }

    return true;
  } catch (error) {
    console.error('Auth check failed:', error);
    return false;
  }
}

/**
 * Gibt aktuellen User zurück
 */
export async function getCurrentUser() {
  try {
    const db = await initDatabase();
    const userInfo = await db.get('auth', 'user_info');
    return userInfo?.value || null;
  } catch (error) {
    console.error('Get user failed:', error);
    return null;
  }
}

/**
 * Gibt Session-Token zurück (für API-Calls)
 */
export async function getSessionToken() {
  try {
    const db = await initDatabase();
    const token = await db.get('auth', 'session_token');
    return token?.value || null;
  } catch (error) {
    console.error('Get token failed:', error);
    return null;
  }
}

/**
 * Aktualisiert gespeicherte Benutzerdaten in IndexedDB
 */
export async function updateStoredUser(updates) {
  const db = await initDatabase();
  const userInfo = await db.get('auth', 'user_info');
  if (!userInfo?.value) return null;

  const updatedUser = { ...userInfo.value, ...updates };
  await db.put('auth', {
    key: 'user_info',
    value: updatedUser,
    updatedAt: new Date().toISOString()
  });

  window.dispatchEvent(new Event('auth-changed'));
  return updatedUser;
}

/**
 * Hilfsfunktion zum sicheren Parsen von JSON-Antworten
 */
async function parseResponseJson(response, fallbackErrorMessage) {
  const text = await response.text();
  let data;
  try {
    data = text ? JSON.parse(text) : {};
  } catch (err) {
    data = { error: 'Ungültige Antwort vom Server' };
  }

  if (!response.ok) {
    throw new Error(data.error || fallbackErrorMessage);
  }
  return data;
}

/**
 * Analytics-Einwilligung speichern
 */
export async function updateAnalyticsConsent(consent) {
  const token = await getSessionToken();
  if (!token) {
    throw new Error('Nicht angemeldet');
  }

  const response = await fetch(`${API_BASE_URL}/user/analytics-consent`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`
    },
    body: JSON.stringify({ consent })
  });

  const data = await parseResponseJson(response, 'Datenschutzeinstellung konnte nicht gespeichert werden');
  await updateStoredUser({
    analyticsConsent: data.analyticsConsent,
    analyticsConsentAsked: data.analyticsConsentAsked
  });

  return data;
}

/**
 * Profil vervollständigen
 */
export async function completeProfile(firstname, lastname) {
  try {
    const token = await getSessionToken();
    
    if (!token) {
      throw new Error('Nicht angemeldet');
    }

    const response = await fetch(`${API_BASE_URL}/auth/profile`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ firstname, lastname })
    });

    const data = await parseResponseJson(response, 'Profil-Update fehlgeschlagen');
    
    // Update user info in IndexedDB
    const db = await initDatabase();
    await db.put('auth', {
      key: 'user_info',
      value: data.user,
      updatedAt: new Date().toISOString()
    });

    return { success: true, user: data.user };
  } catch (error) {
    console.error('Complete profile failed:', error);
    throw error;
  }
}

/**
 * Passwort ändern
 */
export async function changePassword(currentPassword, newPassword) {
  try {
    const token = await getSessionToken();
    
    if (!token) {
      throw new Error('Nicht angemeldet');
    }

    const response = await fetch(`${API_BASE_URL}/auth/password`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ currentPassword, newPassword })
    });

    const data = await parseResponseJson(response, 'Passwort-Änderung fehlgeschlagen');
    // Älteres Token wurde mit beendet – der Server liefert gleich eine neue Anmeldung für dieses Gerät
    if (data.session?.token) await saveSession(data.session);
    return { success: true, message: data.message };
  } catch (error) {
    console.error('Change password failed:', error);
    throw error;
  }
}

/**
 * Namensänderung anfordern (wird an Admin gesendet)
 */
export async function requestNameChange(firstname, lastname) {
  try {
    const token = await getSessionToken();

    if (!token) {
      throw new Error('Nicht angemeldet');
    }

    const response = await fetch(`${API_BASE_URL}/user/name-change-request`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ firstname, lastname })
    });

    return await parseResponseJson(response, 'Namensänderung konnte nicht gesendet werden');
  } catch (error) {
    console.error('Request name change failed:', error);
    throw error;
  }
}

/**
 * E-Mail-Adressänderung anfordern (wird an Admin gesendet)
 */
export async function requestEmailChange(email) {
  try {
    const token = await getSessionToken();

    if (!token) {
      throw new Error('Nicht angemeldet');
    }

    const response = await fetch(`${API_BASE_URL}/user/email-change-request`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ email })
    });

    return await parseResponseJson(response, 'E-Mail-Änderung konnte nicht gesendet werden');
  } catch (error) {
    console.error('Request email change failed:', error);
    throw error;
  }
}
